import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig, MainFilter } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo, useState } from 'react';
import { useLazyGetInvoicedFeesByCaseAndAssignmentReportQuery } from 'store/api/reportingOperations';
import { InvoicedFeesByCaseAndAssignmentRow, InvoicedFeesByCaseAndAssignmentTotals } from 'types/reporting/operations';
import { ReportPage } from 'types/reporting/reportPage';
import { t } from 'utils/getTranslations';

import {
    ReportCaseLink,
    ReportContactLink,
    ReportNumberCell,
    TONED_COLUMN_META,
    tonedCell,
    GREEN,
} from '../reportCells';
import {
    caseFilter,
    clientFilter,
    DATE_BASIS_SELECTOR,
    dateBasisFilter,
    periodFilter,
    periodQuickFilter,
    quickFiltersFrom,
    searchSelectors,
    mainFilterDefaults,
} from '../reportFilters';
import { inBaseCurrency, rowCurrencies, useReportBaseCurrency } from '../reportCurrency';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';

type Row = InvoicedFeesByCaseAndAssignmentRow;
type Totals = InvoicedFeesByCaseAndAssignmentTotals;

const REPORT_KEY = 'invoiced-fees-by-case-and-assignment';

function buildFilterConfig(): FilterConfig {
    const filters: MainFilter[] = [dateBasisFilter(), periodFilter(), clientFilter(), caseFilter()];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        periodQuickFilter: periodQuickFilter({ defaultPeriod: 'current-year' }),
        quickFilters: quickFiltersFrom(filters, { dateBasis: 'reporting.filter.date-basis' }),
        searchBarSelectors: searchSelectors('caseNumber', 'caseName', 'clientName', 'assignmentName'),
        filters,
    };
}

function buildColumns(rows: Row[] | undefined, currency: string | null): DataTableColumnDef<Row, Totals>[] {
    return [
        {
            id: 'clientNumber',
            accessorKey: 'clientNumber',
            header: t('reporting.column.client-number'),
            meta: { widthWeight: 0.5, minPx: 90 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientNumber}</ReportContactLink>
            ),
        },
        {
            id: 'clientName',
            accessorKey: 'clientName',
            header: t('reporting.column.client-name'),
            meta: { widthWeight: 1.2, minPx: 160 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientName}</ReportContactLink>
            ),
        },
        {
            id: 'caseNumber',
            accessorKey: 'caseNumber',
            header: t('reporting.column.case-number'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId} wrapLabel>
                    {row.original.caseNumber}
                </ReportCaseLink>
            ),
        },
        {
            id: 'caseName',
            accessorKey: 'caseName',
            header: t('reporting.column.case-name'),
            meta: { widthWeight: 1.4, minPx: 180 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId}>{row.original.caseName}</ReportCaseLink>
            ),
        },
        {
            id: 'assignmentName',
            accessorKey: 'assignmentName',
            header: t('reporting.column.assignment'),
            meta: { widthWeight: 1.2, minPx: 160 },
        },
        {
            id: 'invoicedHours',
            accessorKey: 'invoicedHours',
            header: t('reporting.column.invoiced-hours'),
            meta: {
                ...TONED_COLUMN_META,
                widthWeight: 0.8,
                minPx: 130,
                textAlign: 'right',
                nullHandling: 'nullsLast',
                renderTotal: (totals) => <ReportNumberCell value={totals?.invoicedHours} />,
            },
            cell: tonedCell(rows, (row) => row.invoicedHours, GREEN),
        },
        {
            id: 'invoicedAmountCaseCurrency',
            accessorKey: 'invoicedAmountCaseCurrency',
            header: t('reporting.column.invoiced-amount-cc'),
            meta: { ...TONED_COLUMN_META, widthWeight: 0.9, minPx: 150, textAlign: 'right', nullHandling: 'nullsLast' },
            cell: tonedCell(rows, (row) => row.invoicedAmountCaseCurrency, GREEN),
        },
        {
            id: 'caseCurrency',
            accessorKey: 'caseCurrency',
            header: t('reporting.column.case-currency'),
            meta: {
                fixedPx: 130,
                renderTotal: () => <EllipsisTooltip>{rowCurrencies(rows, (row) => row.caseCurrency)}</EllipsisTooltip>,
            },
        },
        {
            id: 'invoicedAmount',
            accessorKey: 'invoicedAmount',
            header: inBaseCurrency(t('reporting.column.invoiced-amount'), currency),
            meta: {
                ...TONED_COLUMN_META,
                widthWeight: 0.9,
                minPx: 150,
                textAlign: 'right',
                nullHandling: 'nullsLast',
                renderTotal: (totals) => <ReportNumberCell value={totals?.invoicedAmount} />,
            },
            cell: tonedCell(rows, (row) => row.invoicedAmount, GREEN),
        },
    ];
}

export function InvoicedFeesByCaseAndAssignmentReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetInvoicedFeesByCaseAndAssignmentReportQuery();
    const [initialSearchParams] = useState(() => mainFilterDefaults([`${DATE_BASIS_SELECTOR}==(INVOICE_DATE)`]));
    const filterConfig = useMemo(() => buildFilterConfig(), []);
    const currency = useReportBaseCurrency(data?.totals?.baseCurrency ?? data?.content[0]?.baseCurrency);
    const columns = useMemo(() => buildColumns(data?.content, currency), [data, currency]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)} baseCurrency={currency}>
            <ListTableWithFilters<Row, ReportPage<Row, Totals>>
                data={data}
                belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                isLoading={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
                initialSearchParams={initialSearchParams}
                columns={columns}
                columnConfigId={`report-${REPORT_KEY}`}
                enableColumnPicker
                enableColumnReorder
                enableColumnResize
                pluralTextId="reporting.assignments"
                singularTextId="reporting.assignment"
                noEntriesId="reporting.no-rows"
                noEntriesBodyId="reporting.no-rows-body"
            />
        </ReportLayout>
    );
}
